"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

type Props = {
  imageUrls: string[];
  alt: string;
  sizes: string;
  className?: string;
  /** Карусель над сгибом: priority + eager для LCP (шапка сравнения) */
  imagePriority?: boolean;
};

type RemoteHeroImageProps = {
  src: string;
  alt: string;
  sizes: string;
  priority?: boolean;
  imgClassName: string;
  /** carousel: серый блок; lightbox: на тёмном фоне */
  skeletonTone: "panel" | "dark";
};

/** Смена `src` — через `key={src}` у родителя; без сброса состояния в эффекте */
function GalleryRemoteImage({ src, alt, sizes, priority, imgClassName, skeletonTone }: RemoteHeroImageProps) {
  const t = useTranslations("ComparePage");
  const [ready, setReady] = useState(false);

  const skeletonBg = skeletonTone === "dark" ? "bg-white/12" : "bg-gray-200 dark:bg-gray-800";
  const eager = priority ?? false;

  return (
    <>
      <span className="sr-only" aria-live="polite">
        {!ready ? t("galleryLoading") : ""}
      </span>
      {!ready ? <div className={cn("absolute inset-0 z-[1] animate-pulse", skeletonBg)} aria-hidden /> : null}
      <Image
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        className={cn(imgClassName, "transition-[opacity,transform] duration-300 ease-out", ready ? "opacity-100" : "opacity-0")}
        draggable={false}
        preload={eager}
        {...(eager
          ? ({
              loading: "eager" as const,
              fetchPriority: "high" as const,
            })
          : {})}
        onLoad={() => setReady(true)}
        onError={() => setReady(true)}
      />
    </>
  );
}

/** Карусель фото в сравнении: заполняет ячейку (object-cover), стрелки, точки, лайтбокс целиком. */
export default function CompareCarGallery({ imageUrls, alt, sizes, className, imagePriority }: Props) {
  const t = useTranslations("ComparePage");
  const urls = imageUrls.filter(Boolean);
  const n = urls.length;
  const [i, setI] = useState(0);
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIdx, setLbIdx] = useState(0);

  /** Индекс в пределах [0 … n−1]; при укорочении списка url не дергаем setState в эффектах */
  const viewIndex = n === 0 ? 0 : Math.min(Math.max(0, i), n - 1);

  function goCarousel(delta: number) {
    if (n <= 0) return;
    setI((prev) => (prev + delta + n) % n);
  }

  function openLightbox() {
    if (n < 1) return;
    setLbIdx(viewIndex);
    setLbOpen(true);
  }

  useEffect(() => {
    if (!lbOpen || n <= 0) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setLbOpen(false);
      if (e.key === "ArrowRight") setLbIdx((prev) => (prev + 1 + n) % n);
      if (e.key === "ArrowLeft") setLbIdx((prev) => (prev - 1 + n) % n);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lbOpen, n]);

  if (n < 1) {
    return (
      <div
        className={cn(
          "flex aspect-[16/10] w-full items-center justify-center rounded-xl bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-600",
          className,
        )}
      >
        <Maximize2 size={28} strokeWidth={1.25} aria-hidden />
      </div>
    );
  }

  return (
    <>
      <div className={cn("group/carousel relative isolate aspect-[16/10] w-full overflow-hidden rounded-xl bg-gray-50 dark:bg-gray-900/80", className)}>
        <button
          type="button"
          onClick={openLightbox}
          className="relative block h-full w-full cursor-zoom-in outline-none focus-visible:ring-2 focus-visible:ring-[#E63946]/50"
          aria-haspopup="dialog"
          aria-label={t("galleryFullscreen")}
        >
          <GalleryRemoteImage
            key={urls[viewIndex]}
            src={urls[viewIndex]}
            alt={alt}
            sizes={sizes}
            skeletonTone="panel"
            priority={imagePriority ?? false}
            imgClassName="object-cover object-center group-hover/carousel:scale-[1.01]"
          />
        </button>

        {n > 1 ? (
          <>
            <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center gap-1.5 px-10">
              {urls.map((u, idx) => (
                <button
                  key={`dot-${u}-${idx}`}
                  type="button"
                  className={cn(
                    "pointer-events-auto h-1.5 w-1.5 shrink-0 rounded-full transition-colors",
                    idx === viewIndex ? "bg-[#E63946]" : "bg-white/75 ring-1 ring-black/25 dark:bg-gray-700 dark:ring-white/20",
                  )}
                  aria-label={t("galleryGoToSlide", { n: idx + 1 })}
                  aria-current={idx === viewIndex ? true : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    setI(idx);
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goCarousel(-1);
              }}
              className="absolute left-1 top-1/2 z-[2] flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/92 text-gray-800 shadow backdrop-blur transition hover:bg-white dark:bg-gray-900/92 dark:text-gray-100 dark:hover:bg-gray-900"
              aria-label={t("galleryPrev")}
            >
              <ChevronLeft size={22} aria-hidden />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goCarousel(1);
              }}
              className="absolute right-1 top-1/2 z-[2] flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/92 text-gray-800 shadow backdrop-blur transition hover:bg-white dark:bg-gray-900/92 dark:text-gray-100 dark:hover:bg-gray-900"
              aria-label={t("galleryNext")}
            >
              <ChevronRight size={22} aria-hidden />
            </button>
          </>
        ) : (
          <div className="pointer-events-none absolute bottom-2 right-2 rounded-md bg-black/35 p-1.5 text-white backdrop-blur-sm">
            <Maximize2 size={14} aria-hidden />
          </div>
        )}
      </div>

      {lbOpen && typeof document !== "undefined"
        ? createPortal(
            <GalleryLightbox urls={urls} idx={lbIdx} setIdx={setLbIdx} onClose={() => setLbOpen(false)} alt={alt} />,
            document.body,
          )
        : null}
    </>
  );
}

function GalleryLightbox({
  urls,
  idx,
  setIdx,
  onClose,
  alt,
}: {
  urls: string[];
  idx: number;
  setIdx: (n: number | ((prev: number) => number)) => void;
  onClose: () => void;
  alt: string;
}) {
  const t = useTranslations("ComparePage");
  const n = urls.length;

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  function go(d: number) {
    setIdx((p) => (p + d + n) % n);
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col bg-black/92 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={t("galleryLightboxTitle")}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="mb-2 flex shrink-0 items-center justify-between gap-3 text-white">
        <p className="text-sm font-medium">{t("galleryLightboxCounter", { current: idx + 1, total: n })}</p>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-2 text-white/85 transition-colors hover:bg-white/10 hover:text-white"
          aria-label={t("galleryClose")}
        >
          <X size={26} aria-hidden />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center">
        {n > 1 ? (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              className="absolute left-2 z-[3] flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm transition hover:bg-white/25 sm:left-6"
              aria-label={t("galleryPrev")}
            >
              <ChevronLeft size={28} aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              className="absolute right-2 z-[3] flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm transition hover:bg-white/25 sm:right-6"
              aria-label={t("galleryNext")}
            >
              <ChevronRight size={28} aria-hidden />
            </button>
          </>
        ) : null}
        <div className="relative mx-auto h-[min(85vh,920px)] w-full max-w-[min(96vw,1200px)] px-14 sm:px-16">
          <GalleryRemoteImage
            key={urls[idx]}
            src={urls[idx]}
            alt={alt}
            sizes="100vw"
            skeletonTone="dark"
            imgClassName="object-contain"
          />
        </div>
      </div>

      <p className="mx-auto mt-3 max-w-3xl truncate px-4 text-center text-xs text-white/75">{alt}</p>
    </div>
  );
}
